import React from 'react';

/**
 * ResponsiveGrid
 *
 * A pre-configured responsive grid component that handles column layouts
 * automatically across different breakpoints. No more manual grid-cols-* juggling!
 *
 * Benefits:
 * - Automatic responsive behavior
 * - Consistent breakpoint usage across app
 * - Self-documenting prop-based API
 * - Handles common patterns (1→2→3, 1→2→4, etc.)
 *
 * Usage:
 * ```tsx
 * <ResponsiveGrid columns={{ mobile: 1, tablet: 2, desktop: 4 }} gap="md">
 *   <div>Column 1</div>
 *   <div>Column 2</div>
 *   <div>Column 3</div>
 *   <div>Column 4</div>
 * </ResponsiveGrid>
 * ```
 */

interface ResponsiveGridProps {
  children: React.ReactNode;
  /**
   * Number of columns at each breakpoint
   * Default: { mobile: 1, tablet: 2, desktop: 3 }
   */
  columns?: {
    mobile?: 1 | 2 | 3 | 4;
    tablet?: 1 | 2 | 3 | 4 | 6;
    desktop?: 1 | 2 | 3 | 4 | 6;
  };
  /**
   * Gap between grid items
   * - 'sm': 1rem (16px)
   * - 'md': 1.5rem (24px)
   * - 'lg': 2rem (32px)
   * - 'xl': 3rem (48px)
   */
  gap?: 'sm' | 'md' | 'lg' | 'xl';
  /**
   * Vertical alignment of grid items
   * - 'start': Align to top
   * - 'center': Center vertically
   * - 'end': Align to bottom
   * - 'stretch': Fill height (default)
   */
  align?: 'start' | 'center' | 'end' | 'stretch';
  /** Additional CSS classes */
  className?: string;
}

export default function ResponsiveGrid({
  children,
  columns = { mobile: 1, tablet: 2, desktop: 3 },
  gap = 'md',
  align = 'stretch',
  className = '',
}: ResponsiveGridProps) {
  // Map column counts to Tailwind classes
  const columnClasses = {
    1: 'grid-cols-1',
    2: 'grid-cols-2',
    3: 'grid-cols-3',
    4: 'grid-cols-4',
    6: 'grid-cols-6',
  };

  const gapClasses = {
    sm: 'gap-4',      // 16px
    md: 'gap-6',      // 24px
    lg: 'gap-8',      // 32px
    xl: 'gap-12',     // 48px
  };

  const alignClasses = {
    start: 'items-start',
    center: 'items-center',
    end: 'items-end',
    stretch: 'items-stretch',
  };

  // Build responsive column classes
  const mobileClass = columnClasses[columns.mobile || 1];
  const tabletClass = columns.tablet ? `sm:${columnClasses[columns.tablet]}` : '';
  const desktopClass = columns.desktop ? `lg:${columnClasses[columns.desktop]}` : '';

  return (
    <div
      className={`grid ${mobileClass} ${tabletClass} ${desktopClass} ${gapClasses[gap]} ${alignClasses[align]} ${className}`}
    >
      {children}
    </div>
  );
}
